import type dayjs from '@dcp/shared/utils/dayjs'
import type { CashPositionMultiPeriodColumn } from '@/hooks/useCashPositionSummaryMultiPeriod'

/** Characters Windows and macOS refuse in a file name. */
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g

/** How many names the "Uses current filters" line spells out before "+N". */
const MAX_LISTED_NAMES = 2

/**
 * File name of a downloaded Cash Position Summary:
 * `YYYY.MM.DD <Org> Cash Position Summary.<ext>`. Characters illegal in file
 * names are dropped from the organization name and whitespace is collapsed, so
 * the browser does not silently rename the file.
 *
 * @param date - the business day the report is run for (`dayjs(businessDate())`)
 */
export function cashPositionReportFileName(orgName: string, date: ReturnType<typeof dayjs>, ext: 'pdf' | 'xlsx'): string {
  const base = `${date.format('YYYY.MM.DD')} ${orgName} Cash Position Summary`
  const clean = base.replace(ILLEGAL_FILENAME_CHARS, '').replace(/\s+/g, ' ').trim()
  return `${clean}.${ext}`
}

/** The parts of a report column the scope line is built from. */
type ScopeColumn = Pick<CashPositionMultiPeriodColumn, 'kind' | 'packId' | 'name'>

/**
 * The "{scope}, {period}" part of the download menu's "Uses current filters: …"
 * line.
 *
 * The scope is read from the report's columns, not from the selected filter ids,
 * because it has to describe what the file contains: the RPC decides which
 * facilities are shown and which packs are complete enough to get a subtotal.
 * Each pack column contributes the pack name (its members are covered by it);
 * each facility column outside a listed pack — a standalone facility, or a
 * member of a pack some of whose facilities were filtered out — contributes the
 * facility name. Names keep column order; more than two are shortened to the
 * first two plus " +N".
 *
 * @param columns - `data.columns` of the multi-period summary the file is built from
 * @param periodLabel - the selected period's label, or null for "all periods"
 */
export function cashPositionFilterText(columns: ScopeColumn[], periodLabel: string | null): string {
  const packIds = new Set(columns.flatMap((c) => (c.kind === 'pack' && c.packId !== null ? [c.packId] : [])))
  const names = columns
    .filter((c) => c.kind === 'pack' || c.packId === null || !packIds.has(c.packId))
    .map((c) => c.name)
  const rest = names.length - MAX_LISTED_NAMES
  const listed = names.slice(0, MAX_LISTED_NAMES).join(', ')
  const scope = names.length === 0 ? 'no facilities' : rest > 0 ? `${listed} +${rest}` : listed
  return `${scope}, ${periodLabel ?? 'all periods'}`
}
